import { Fragment } from 'react'
import {
  Box,
  Grid,
  Stack,
  VStack,
  HStack,
  Text,
  useToast,
  Icon,
  SimpleGrid,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react'
import Sticky from 'react-stickynode'
import { useTranslation, Trans } from 'next-i18next'
import { useRouter } from 'next/router'
import { AiFillCheckCircle } from 'react-icons/ai'
import { saveAs } from 'file-saver'

import { GET_TRAINING } from '@/routes/resources'

import { logEvent, useUTMContext } from '@/lib/gtm'
import env from '@/lib/env'
import requests from '@/lib/requests'

import Form from '@/features/resources/components/Form'
import Widget from '@/features/resources/components/WidgetList/Widget'

import { DEFAULT_TOAST_OPTIONS } from '@/lib/utils/format/toast'

import { usePlatformData, useDetailOfTrainer } from './constants'

export const RESOURCE_TITLE = 'implementation_training'

function TrainingContent(): React.ReactElement {
  const {
    t: tTraining,
    i18n: { language },
  } = useTranslation('training')
  const { t: tResource } = useTranslation('resource')
  const { data: utm } = useUTMContext()

  const toast = useToast()
  const router = useRouter()
  const PLATFORMS = usePlatformData()
  const SERVICE_DATA = useDetailOfTrainer()

  return (
    <Box
      sx={{
        bg: 'secondary.800',
        w: 'full',
        '& > *': {
          px: 0,
        },
      }}
    >
      <Grid
        id="get-resource-container"
        layerStyle={{ md: 'container' }}
        sx={{
          gridTemplateColumns: {
            base: '1fr',
            md: '2fr 1fr',
          },
          gridGap: {
            base: 8,
            sm: 10,
          },
        }}
      >
        <VStack
          spacing={{ base: 8, sm: 10 }}
          layerStyle={{ base: 'container', md: '' }}
          sx={{
            alignItems: 'flex-start',
            color: 'white',
            mb: 8,
            pt: {
              base: 24,
              md: 10,
            },
            pb: 0,
          }}
        >
          <Box>
            <Text
              as="h2"
              sx={{
                fontSize: { base: 'lg', sm: '2xl' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tTraining('details.0.title')}
            </Text>
            <Text
              sx={{
                fontSize: { base: 'md', sm: 'lg' },
              }}
            >
              <Trans t={tTraining} i18nKey="details.0.paragraph.0">
                <Text
                  as="span"
                  sx={{
                    color: 'white',
                    lineHeight: 'inherit',
                    fontWeight: 'medium',
                  }}
                >
                  PDPA Core
                </Text>
              </Trans>
              <br />
              <Trans t={tTraining} i18nKey="details.0.paragraph.1">
                <Text
                  as="span"
                  sx={{
                    color: 'white',
                    lineHeight: 'inherit',
                    fontWeight: 'medium',
                  }}
                >
                  PDPA Core
                </Text>
              </Trans>
            </Text>
          </Box>
          <VStack
            spacing="5"
            layerStyle={{ base: 'container', md: '' }}
            sx={{
              alignItems: 'stretch',
              color: 'white',
              alignSelf: 'center',
              pt: 4,
              maxW: '100vw',
            }}
          >
            <Text
              as="h2"
              sx={{
                fontSize: { base: 'lg', sm: '2xl' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tTraining('contents.title')}
            </Text>

            <Text
              as="h3"
              sx={{
                fontSize: { base: 'md', sm: 'lg' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tTraining('contents.platform.title')}
            </Text>
            <Grid
              sx={{
                gridTemplateColumns: {
                  base: '1fr',
                  md: 'repeat(2, 1fr)',
                },
                gridGap: {
                  base: 5,
                  sm: 7,
                },
              }}
            >
              {PLATFORMS.map((datum, index) => {
                return <Widget key={`${index}`} {...datum} />
              })}
            </Grid>

            <Text
              as="h3"
              sx={{
                fontSize: { base: 'md', sm: 'lg' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tTraining('contents.whatYouGet.title')}
            </Text>

            <Stack
              spacing="4"
              sx={{
                borderRadius: 'md',
                bgColor: 'secondary.950',
                color: 'white',
                px: 6,
                py: 8,
              }}
            >
              {new Array(4).fill(0).map((_, index) => {
                return (
                  <HStack key={`${index}`}>
                    <Icon
                      as={AiFillCheckCircle}
                      color="green.450"
                      fontSize="4xl"
                    />
                    <Text
                      sx={{
                        fontSize: { base: 'md', sm: 'lg' },
                        fontWeight: 'medium',
                      }}
                    >
                      {tTraining(`contents.whatYouGet.list.${index}`)}
                    </Text>
                  </HStack>
                )
              })}
            </Stack>

            <Text
              as="h3"
              sx={{
                fontSize: { base: 'md', sm: 'lg' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tTraining('contents.detail.title')}
            </Text>

            {SERVICE_DATA.map((datum, index) => (
              <Fragment key={index}>
                <HStack spacing="3" sx={{ alignItems: 'stretch' }}>
                  <Box
                    sx={{
                      borderRadius: 'full',
                      bgColor: 'primary.400',
                      minW: 1,
                      maxW: 1,
                    }}
                  />
                  {datum.title && (
                    <Text
                      sx={{
                        fontSize: { base: 'md', sm: 'lg' },
                        fontWeight: 'medium',
                      }}
                    >
                      {datum.title}
                      {datum.subtitle && (
                        <>
                          <br />
                          {datum.subtitle}
                        </>
                      )}
                    </Text>
                  )}
                </HStack>
                <SimpleGrid columns={2} spacing={10}>
                  {datum.leftList && (
                    <UnorderedList>
                      {datum.leftList.map((item, index) => (
                        <ListItem key={index}>{item}</ListItem>
                      ))}
                    </UnorderedList>
                  )}
                  {datum.rightList && (
                    <UnorderedList>
                      {datum.rightList.map((item, index) => (
                        <ListItem key={index}>{item}</ListItem>
                      ))}
                    </UnorderedList>
                  )}
                </SimpleGrid>
              </Fragment>
            ))}
          </VStack>
        </VStack>
        <Sticky
          bottomBoundary="#get-resource-container > *:first-of-type"
          top={120}
        >
          <Form
            id="get-resource"
            resourceTitle={RESOURCE_TITLE}
            onSubmit={async (data) => {
              const response = await requests.post(
                `${env.APP_URL}/api/get-resource/`,
                { ...data, utm, language },
                {},
              )

              const resBody = response.data.data
              const downloadUrl: string = resBody.downloadUrl

              saveAs(downloadUrl)

              toast({
                ...DEFAULT_TOAST_OPTIONS,
                description: tResource('form.successToast'),
              })

              logEvent({
                ga: {
                  category: 'Guideline',
                  action: 'Download success',
                },
                fb: {
                  event: 'Lead',
                },
              })

              router.push(
                {
                  pathname: GET_TRAINING.pathname,
                },
                undefined,
                {
                  locale: language,
                },
              )
            }}
            trans={tTraining}
            formsubmitButton={tTraining('form.submitButton')}
          />
        </Sticky>
      </Grid>
    </Box>
  )
}

export default TrainingContent
